import { SyntheticEvent, useCallback, useMemo, useState } from "react";

import deepClone from "../../helpers/deepClone";
import useWatchState from "../../hooks/useWatchState";
import {
  ICheckboxFilterData,
  ICheckboxFilterItem,
  IFilterCallbackData,
  IFilterTagData,
  IFilterUpdate,
  IRadioFilterData,
  IRadioFilterItem,
  IRangeFilterData,
  resetRadioFilter,
  TFilterWithData,
  TRangeTuple,
} from "../../components";
import {
  rangeFilterItemToTagsDTO,
  resetRangeFilter,
} from "../../components/Navigational/Filters/RangeFilter/RangeFilter";
import {
  checkboxFilterItemToTagsDTO,
  resetCheckboxFilter,
} from "../../components/Navigational/Filters/CheckboxFilter/CheckboxFilter";
import { radioFilterItemToTagsDTO } from "../../components/Navigational/Filters/RadioFilter/RadioFilter";

export type TFilterData = {
  /** Count of items with current filters selected */
  total: number;
  /** Current state/value of filters */
  filterItemsState: TFilterWithData[];
  /** List of tags in sync with selected filters */
  tags: IFilterTagData[];
  /** Method that can be called for reset */
  handleClearAll(): void;
  formSubmitHandler(e: SyntheticEvent): void;
  /** Can be called to update values */
  handleFiltersChange(IFilterCallbackData): void;
  /** Can be called when clearing of some particular tag is needed */
  handleRemoveFilterTag(tag: IFilterTagData): void;
};

const formSubmitHandler = (e: SyntheticEvent): void => e.preventDefault();

export interface IFilterProps {
  /** initial state of filters (watched, pass new link to update) */
  filterItems: TFilterWithData[];
  /** Callback to indicate what was updated */
  onFieldUpdate?(updates: IFilterUpdate[]);
}

export default ({ filterItems, onFieldUpdate }: IFilterProps): TFilterData => {
  const [total, setTotal] = useState<number>(0);
  const [filterItemsState, setFilterState] = useWatchState<TFilterWithData[]>({
    value: filterItems,
  });
  const tags: IFilterTagData[] = useMemo(
    () =>
      filterItemsState
        .map((filterItem) => {
          if (["tile", "checkbox"].includes(filterItem.type)) {
            return checkboxFilterItemToTagsDTO(
              filterItem as ICheckboxFilterData,
            );
          }
          if (filterItem.type === "range") {
            return rangeFilterItemToTagsDTO(filterItem as IRangeFilterData);
          }
          if (filterItem.type === "radio") {
            const radioData = radioFilterItemToTagsDTO(
              filterItem as IRadioFilterData,
            );
            return radioData ? [radioData] : [];
          }
          return [];
        })
        .filter((data) => data.length)
        .flat(),
    [filterItemsState],
  );

  /**
   * Reset all the filters
   */
  const handleClearAll = useCallback(() => {
    setFilterState(
      filterItems.map((filterItem) => {
        if (filterItem.type === "radio") {
          return resetRadioFilter(filterItem as IRadioFilterData);
        }
        if (filterItem.type === "range") {
          return resetRangeFilter(filterItem as IRangeFilterData);
        }

        return resetCheckboxFilter(filterItem as ICheckboxFilterData);
      }),
    );
    setTotal(0);
  }, [filterItems, setFilterState]);

  /**
   * Update filter state on tag remove
   */
  const handleRemoveFilterTag = useCallback(
    (curTag: IFilterTagData): void => {
      const tagFilterData = deepClone(filterItemsState).find(
        (filterItem) => filterItem.name === curTag.category,
      );

      /** Radio */
      if (tagFilterData.type === "radio") {
        setFilterState(
          deepClone(filterItemsState).map((filterItem: IRadioFilterData) =>
            filterItem.name === curTag.category
              ? resetRadioFilter(filterItem)
              : filterItem,
          ),
        );
        return;
      }

      /** Range */
      if (tagFilterData.type === "range") {
        const rangeIndex = Number(curTag.id);
        const rangeKey = rangeIndex === 0 ? "min" : "max";
        tagFilterData.value[rangeIndex] = tagFilterData.defaultValue
          ? tagFilterData.defaultValue[rangeIndex]
          : tagFilterData[rangeKey] || "0";
        setFilterState(
          deepClone(filterItemsState).map((filterItem: IRangeFilterData) =>
            filterItem.name === curTag.category
              ? { ...filterItem, ...tagFilterData }
              : filterItem,
          ),
        );
        return;
      }

      /** Tile, Checkbox */
      const newFilterItems = (tagFilterData as ICheckboxFilterData).items.map(
        (item) => (item.id === curTag.id ? { ...item, checked: false } : item),
      );
      const newState = deepClone(filterItemsState).map(
        (filterItem: ICheckboxFilterData) =>
          filterItem.name === curTag.category
            ? { ...filterItem, items: newFilterItems }
            : filterItem,
      );
      setFilterState(newState);
    },
    [filterItemsState, setFilterState],
  );

  const handleCheckboxChange = useCallback(
    (categoryName: string, newValues: ICheckboxFilterItem[]): void => {
      /**
       * Set new values for correct category
       */
      const newFilterItems: TFilterWithData[] = deepClone(filterItemsState).map(
        (item: ICheckboxFilterData) =>
          item.categoryName === categoryName
            ? { ...item, items: newValues }
            : item,
      );

      setFilterState(newFilterItems);

      /**
       * Filter out only checkbox filter items,
       *  then flatten array,
       *  then calculate total items for selected filters
       */
      const flatItemArray = newFilterItems.reduce(
        (flatItems: ICheckboxFilterItem[], filter: ICheckboxFilterData) =>
          flatItems.concat(filter.items),
        [],
      );

      setTotal(
        flatItemArray.reduce(
          (totalItemsCount: number, item: ICheckboxFilterItem) =>
            item && item.checked && item.itemsCount
              ? totalItemsCount + item.itemsCount
              : totalItemsCount,
          0,
        ),
      );
    },
    [filterItemsState, setFilterState],
  );

  const handleRadioChange = useCallback(
    (categoryName: string, values: IRadioFilterItem[]): void => {
      /**
       * Set new values for radio
       */
      const newFilterItems: TFilterWithData[] = deepClone(filterItemsState).map(
        (item: IRadioFilterData) =>
          item.categoryName === categoryName
            ? {
                ...item,
                items: values,
              }
            : item,
      );

      setFilterState(newFilterItems);
    },
    [filterItemsState, setFilterState],
  );

  const handleRangeChange = useCallback(
    (categoryName: string, values: TRangeTuple): void => {
      /**
       * Set new values for range [string, string]
       */
      const newFilterItems = deepClone(filterItemsState).map((item) =>
        item.categoryName === categoryName ? { ...item, value: values } : item,
      );

      setFilterState(newFilterItems);
    },
    [filterItemsState, setFilterState],
  );

  /**
   * Select strategy on filter change, based on filter type
   */
  const handleFiltersChange = useCallback(
    ({
      categoryName,
      filterType,
      values,
      updates,
    }: IFilterCallbackData): void => {
      switch (filterType) {
        case "tile":
        case "checkbox":
          handleCheckboxChange(categoryName, values as ICheckboxFilterItem[]);
          break;
        case "radio":
          handleRadioChange(categoryName, values as IRadioFilterItem[]);
          break;
        case "range":
          handleRangeChange(categoryName, values as TRangeTuple);
          break;
        default:
          break;
      }
      onFieldUpdate && onFieldUpdate({ ...updates });
    },
    [handleCheckboxChange, handleRadioChange, handleRangeChange, onFieldUpdate],
  );

  return {
    total,
    tags,
    filterItemsState,
    handleClearAll,
    formSubmitHandler,
    handleFiltersChange,
    handleRemoveFilterTag,
  };
};
